import { useCurrentStoreId } from '@hooks/useCurrentStore'
import { useScanId } from '@hooks/useScan'
import { useSelectedDay } from '@hooks/useSelectedDay'

import httpClient, { type HttpClient, type UrlParam } from './HttpClient'

export function viewBaseUrl(storeId: string, scanId: string, day: string | undefined) {
  return day ? `/api/store/${storeId}/day/${day}` : `/api/store/${storeId}/scan/${scanId}`
}

/**
 * Identifies what the map is showing, for query keys and remounts.
 *
 * @param floorDay the day whose floor a single scan is drawn on, see recalculateShelvesInFrame
 */
export function viewKeyOf(scanId: string, day: string | undefined, floorDay?: string) {
  if (day) return `day:${day}`
  return floorDay ? `${scanId}@${floorDay}` : scanId
}

/**
 * For the reads with a day version: layout, shelf, search and todos. They follow the selected day or
 * its single scan, except that todos pass `latestDayOnly`: they are work to do now. Everything else
 * (panorama) stays on useScanContextAwareHttpClient until it has a day version too.
 */
function useViewContextAwareHttpClient({ latestDayOnly = false, ignoreDayScan = false } = {}): Pick<HttpClient, 'get'> & { storeId: string; viewKey: string } {
  const storeId = useCurrentStoreId()
  const pathScanId = useScanId()
  const { day: selectedDay, dayScan: selectedScan, todoDay } = useSelectedDay()
  const dayScan = ignoreDayScan ? undefined : selectedScan

  let day = dayScan ? undefined : selectedDay
  if (latestDayOnly) day = todoDay

  const scanId = dayScan ?? pathScanId
  const baseUrl = viewBaseUrl(storeId, scanId, day)

  return {
    storeId,
    viewKey: viewKeyOf(scanId, day, dayScan && selectedDay),
    get: async (url: UrlParam) => httpClient.get(`${baseUrl}${url}`),
  }
}

export default useViewContextAwareHttpClient
